import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HeartPulse, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import LangSwitcher from "@/components/LangSwitcher";

export default function RegisterHospital() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [form, setForm] = useState({
    name: "", city: "", contact_email: "", contact_phone: "",
    admin_name: "", admin_password: "",
    commune: "", quartier: "", avenue: "", ville: "", province: "", pays: "RDC",
  });

  useEffect(() => {
    axios.get(`${API}/ministry/provinces`).then(r => setProvinces(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.admin_password.length < 8) {
      toast.error("Mot de passe trop court (8 caractères min)");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/hospitals/register`, form);
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="px-6 md:px-12 py-5 flex items-center justify-between border-b border-[#E5EBE7] bg-white">
        <Link to="/" className="flex items-center gap-2" data-testid="reg-logo">
          <div className="w-9 h-9 rounded-xl bg-[#1E4631] grid place-items-center">
            <HeartPulse className="w-5 h-5 text-white"/>
          </div>
          <span className="font-[Outfit] font-bold text-lg">KWIKILA Care</span>
        </Link>
        <div className="flex items-center gap-3">
          <LangSwitcher/>
          <Link to="/" className="text-sm text-[#5C6B61] hover:text-[#1E4631] inline-flex items-center gap-1.5" data-testid="reg-back">
            <ArrowLeft className="w-4 h-4"/> {t.reg.back}
          </Link>
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-6 py-12 md:py-20">
        {done ? (
          <div className="bg-white rounded-2xl border border-[#E5EBE7] p-10 text-center" data-testid="reg-success">
            <div className="w-16 h-16 rounded-full bg-[#2E7D32]/10 grid place-items-center mx-auto mb-5">
              <CheckCircle2 className="w-8 h-8 text-[#2E7D32]"/>
            </div>
            <h1 className="text-3xl font-[Outfit] font-bold mb-3">Demande envoyée !</h1>
            <p className="text-[#5C6B61] mb-8">{t.reg.success}</p>
            <Button onClick={() => navigate("/")} className="bg-[#1E4631] hover:bg-[#163424] text-white" data-testid="reg-success-home">
              {t.reg.back}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="text-4xl font-[Outfit] font-bold mb-3">{t.reg.title}</h1>
            <p className="text-[#5C6B61] mb-8">{t.reg.subtitle}</p>
            <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5EBE7] p-8 space-y-5" data-testid="reg-form">
              <div className="grid md:grid-cols-2 gap-5">
                <div><Label>{t.reg.h_name}</Label><Input required value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="reg-name"/></div>
                <div><Label>{t.reg.h_city}</Label><Input required value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} placeholder="Kinshasa, Lubumbashi..." data-testid="reg-city"/></div>
                <div><Label>{t.reg.h_email}</Label><Input type="email" required value={form.contact_email} onChange={(e)=>setForm({...form, contact_email: e.target.value})} data-testid="reg-email"/></div>
                <div><Label>{t.reg.h_phone}</Label><Input required value={form.contact_phone} onChange={(e)=>setForm({...form, contact_phone: e.target.value})} placeholder="+243..." data-testid="reg-phone"/></div>
              </div>
              <div className="border-t border-[#E5EBE7] pt-5 space-y-4">
                <div>
                  <h3 className="font-[Outfit] font-semibold">Adresse physique de l'hôpital</h3>
                  <p className="text-xs text-[#5C6B61] mt-0.5">Facultatif — utile pour l'orientation des patients et le suivi géographique.</p>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div><Label>Commune</Label><Input value={form.commune} onChange={(e)=>setForm({...form, commune: e.target.value})} placeholder="Ex: Gombe, Kalamu…" data-testid="reg-commune"/></div>
                  <div><Label>Quartier</Label><Input value={form.quartier} onChange={(e)=>setForm({...form, quartier: e.target.value})} placeholder="Ex: Q.7 Lemba" data-testid="reg-quartier"/></div>
                  <div className="md:col-span-2"><Label>Avenue et N°</Label><Input value={form.avenue} onChange={(e)=>setForm({...form, avenue: e.target.value})} placeholder="Ex: Av. Kasa-Vubu N°123" data-testid="reg-avenue"/></div>
                  <div><Label>Ville</Label><Input value={form.ville} onChange={(e)=>setForm({...form, ville: e.target.value})} placeholder="Ex: Kinshasa" data-testid="reg-ville"/></div>
                  <div>
                    <Label>Province</Label>
                    <Select value={form.province || "__none__"} onValueChange={(v)=>setForm({...form, province: v === "__none__" ? "" : v})}>
                      <SelectTrigger data-testid="reg-province"><SelectValue placeholder="Sélectionner une province"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Non précisé —</SelectItem>
                        {provinces.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Pays</Label><Input value={form.pays} onChange={(e)=>setForm({...form, pays: e.target.value})} data-testid="reg-pays"/></div>
                </div>
              </div>
              <div className="border-t border-[#E5EBE7] pt-5 space-y-5">
                <h3 className="font-[Outfit] font-semibold">Compte administrateur</h3>
                <div className="grid md:grid-cols-2 gap-5">
                  <div><Label>{t.reg.a_name}</Label><Input required value={form.admin_name} onChange={(e)=>setForm({...form, admin_name: e.target.value})} data-testid="reg-admin-name"/></div>
                  <div><Label>{t.reg.a_pw}</Label><Input type="password" required minLength={8} value={form.admin_password} onChange={(e)=>setForm({...form, admin_password: e.target.value})} data-testid="reg-admin-pw"/></div>
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#1E4631] hover:bg-[#163424] text-white py-6 font-semibold" data-testid="reg-submit">
                {loading ? "..." : t.reg.submit}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
